import { useWebsiteSettings } from 'client/store'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { PageTypeEnum, WebsitePageType } from 'common/enums/PageTypeEnum'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQueryKey } from 'src/editor/api/hooks/use-query-website-pages'
import { createWebsitePage } from 'src/editor/api/website-pages-api'
import { DOMAIN_PLACEHOLDER } from 'src/editor/shared/constants/website-consts'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { ModalWindow } from 'src/editor/shared/ui-lib/modal-window'
import { generatePathFromText } from 'src/editor/shared/utils/generate-path-from-text'
import { sanitizeUrlInput } from 'src/editor/shared/utils/sanitize-url-input'
import { getWebsiteDomain } from 'src/editor/store/website-settings/website-settings-selector'
import { RadioPageElement } from './radio-page-element'
import { creatablePageTypes } from '../constants/creatable-page-types'

export interface CreatePageModalProps {
  isVisible: boolean
  websiteId: number
  currentWebsiteLocale: PageLocaleEnum
  onClose?: () => void
}

export const CreatePageModal = ({
  isVisible,
  websiteId,
  currentWebsiteLocale,
  onClose,
}: CreatePageModalProps) => {
  const { t } = useTranslation()
  const websiteDomain = useWebsiteSettings(getWebsiteDomain)

  const [selectedPageType, setSelectedPageType] =
    React.useState<WebsitePageType>(PageTypeEnum.WebsiteDefault)
  const [name, setName] = React.useState('')
  const [url, setUrl] = React.useState('')

  // Reset form fields to initial state
  const resetForm = React.useCallback(() => {
    setSelectedPageType(PageTypeEnum.WebsiteDefault)
    setName('')
    setUrl('')
  }, [])

  // Reset form when modal opens
  React.useEffect(() => {
    if (isVisible) {
      resetForm()
    }
  }, [isVisible, resetForm])

  const isDisabled = !(selectedPageType && name && url && websiteId)

  const { fetcher, isLoading } = useMutationWithValidation({
    requestFunction: createWebsitePage,
    successToastMessage: t(
      'website_settings.sidebar.page_settings.created_successfully',
    ),
    handleSuccess: () => {
      resetForm()
      onClose?.()
    },
    invalidateKeyOnSuccess: websitePagesQueryKey(websiteId),
  })
  const handleCreatePage = () => {
    if (!isDisabled) {
      fetcher({
        websiteId,
        pageType: selectedPageType,
        name,
        url,
        locale: currentWebsiteLocale,
      })
    }
  }

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(
      `https://${websiteDomain?.name || DOMAIN_PLACEHOLDER}/${url}`,
    )
  }

  const handleFocusUrlInput = () => {
    if (!url) {
      setUrl(generatePathFromText(name))
    }
  }

  return (
    <ModalWindow
      isOpened={isVisible}
      onClose={onClose}
      title={t('pages_management.create_page_title')}
      cancelButtonProps={{
        onClick: onClose,
        children: t('global.cancel'),
      }}
      confirmButtonProps={{
        onClick: () => {
          handleCreatePage()
        },
        disabled: isDisabled,
        isLoading,
        children: t('core.header.save'),
      }}
    >
      <TextInputControl
        wrapperProps={{
          className: 'mt-1 mb-3',
          labelText: t('pages_management.create_page_name_label'),
          required: true,
        }}
        id="name"
        value={name}
        update={value => setName(value)}
      />
      <TextInputControl
        wrapperProps={{
          className: 'w-full mt-1 mb-3',
          labelText: t('pages_management.create_page_url_label'),
          required: true,
        }}
        appendix={`https://${websiteDomain?.name || DOMAIN_PLACEHOLDER}/`}
        id="url"
        value={url}
        update={value => setUrl(sanitizeUrlInput(value))}
        onCopy={handleCopyUrl}
        onFocus={handleFocusUrlInput}
      />
      {/* NOTE: Temporary hide page type selection */}
      <h4 className="text-heading-4 text-textPrimary">
        <span>{t('pages_management.page_type_label')}</span>
        <span className="text-error-500"> *</span>
      </h4>
      <div>
        {creatablePageTypes.map(({ type, label }) => (
          <RadioPageElement
            key={type}
            isChecked={selectedPageType === type}
            onClick={() => setSelectedPageType(type)}
            name={t(label)}
            pageType={type}
          />
        ))}
      </div>
    </ModalWindow>
  )
}
